import React, { useCallback, useContext, useMemo, useState } from 'react'

import { Input } from 'tools/Input'
import { MaxViewPage } from 'tools/Uniform'
import { isNotId } from 'utils/string'

import GlobalContext from 'reducer/global'

import OrgContext, { R_ORG } from '../reducer'
import OrgClass from './OrgClass'

export default function Settings() {
  const [{ user }] = useContext(GlobalContext)
  const isAdmin = user.roles.size > 0

  const [org, dispatch] = useContext(OrgContext)
  const { onSave } = org
  const onChange = useCallback(
    (value, { token: key }) => {
      dispatch({ type: R_ORG.MERGE, value: { [key]: value } })
    },
    [dispatch]
  )
  const orgId = org.id
  const makeDataVars = useCallback(
    ({ token, value }) => {
      const vars = {
        data: { orgId, type: token, value: JSON.stringify({ value }) }
      }
      return vars
    },
    [orgId]
  )

  const loading = isNotId(orgId)

  return (
    <MaxViewPage>
      <div className="mb4 flex-items flex-wrap ga2 gh3-m">
        <h1>
          <span className="dn di-m">{org.name}</span> Settings
        </h1>
        {org.status === 'closed' && (
          <span className="accent">
            <i className="fa-solid fa-lock fa-lg pr2" />
            <span className="">org closed</span>
          </span>
        )}
        <div className="ml-auto">
          <OrgClass />
        </div>
      </div>
      <div className="grid icols-2 gv2 items-center">
        <div className="b tr mr2 items-center">Name:</div>
        <Input
          wrapClass="normal"
          loading={loading}
          value={org.name}
          onChange={onChange}
          onSave={onSave}
          token="name"
          component="org"
        />
        <div className="b tr mr2 items-center">Account ID:</div>
        <div className="flex-items">
          <Input
            wrapClass="normal"
            value={org.shortId}
            loading={loading}
            onChange={onChange}
            onSave={onSave}
            token="shortId"
            component="org"
          />
          {isAdmin && <div className="f1 minimize ml-auto">{org.id}</div>}
        </div>
        <div className="b tr mr2 items-center">Phone Number:</div>
        <Input
          wrapClass="normal"
          value={org.phone}
          loading={loading}
          onChange={onChange}
          onSave={onSave}
          token="phone"
          component="data"
          makeVars={makeDataVars}
        />
        <div className="b tr mr2 items-center">Website:</div>
        <Input
          wrapClass="normal"
          value={org.website}
          loading={loading}
          onChange={onChange}
          onSave={onSave}
          token="website"
          component="data"
          makeVars={makeDataVars}
        />
        <EditAddress
          type="companyaddr"
          makeVars={makeDataVars}
          onChange={onChange}
          label="Company Address"
          loading={loading}
        />
        <EditAddress
          type="billingaddr"
          makeVars={makeDataVars}
          onChange={onChange}
          label="Billing Address"
          loading={loading}
        />
        <h2 className="span-all mt4 mb3">Alternate Contacts</h2>
        <div className="span-all i f3">
          In order to keep the right people in the loop, you can add additional
          contacts for each of these needs, otherwise communications will to go to
          the account owner(s). To add an alternate contact, click the{' '}
          <i className="fas fa-plus" /> sign next to the contact.
        </div>
        <Contact label="Admin" onChange={onChange} makeVars={makeDataVars} />
        <Contact label="Billing" onChange={onChange} makeVars={makeDataVars} />
        <Contact label="Operations" onChange={onChange} makeVars={makeDataVars} />
        <Contact label="Security" onChange={onChange} makeVars={makeDataVars} />
      </div>
    </MaxViewPage>
  )
}

function EditAddress({ type, makeVars, onChange, label, loading }) {
  const [org] = useContext(OrgContext)
  const changer = useCallback(
    (value, { token: key }) => {
      const address = org[type]
      onChange({ ...address, [key]: value }, { token: type })
    },
    [onChange, type, org]
  )
  const onSave = useCallback(
    (args, value, good, bad) =>
      org.onSave(
        { ...args, token: type },
        { ...org[type], [args.token]: value },
        good,
        bad
      ),
    [org, type]
  )
  const addr = org[type]

  return (
    <>
      <div className="b tr mr2 items-start h-100 pt2">{label}:</div>
      <div>
        <Input
          wrapClass="normal"
          value={addr.street}
          loading={loading}
          onChange={changer}
          onSave={onSave}
          token="street"
          placeholder="Street"
          component="data"
          makeVars={makeVars}
        />
        <div className="grid cols-2 ga2 mt2">
          <div className="">
            <Input
              wrapClass="normal"
              value={addr.city}
              loading={loading}
              onChange={changer}
              onSave={onSave}
              token="city"
              component="data"
              makeVars={makeVars}
              placeholder="City"
            />
          </div>
          <div className="">
            <Input
              wrapClass="normal"
              value={addr.state}
              loading={loading}
              onChange={changer}
              onSave={onSave}
              component="data"
              makeVars={makeVars}
              token="state"
              placeholder="State"
            />
          </div>
          <div className="">
            <Input
              wrapClass="normal"
              value={addr.postal}
              loading={loading}
              onChange={changer}
              onSave={onSave}
              component="data"
              makeVars={makeVars}
              token="postal"
              placeholder="Postal Code"
            />
          </div>
          <div className="">
            <Input
              wrapClass="normal"
              value={addr.country}
              loading={loading}
              onChange={changer}
              onSave={onSave}
              component="data"
              makeVars={makeVars}
              token="country"
              placeholder="Country"
            />
          </div>
        </div>
      </div>
    </>
  )
}

// function EditAddress({ type, makeVars, onChange, label }) {
function Contact({ label, makeVars, onChange }) {
  const [org] = useContext(OrgContext)
  const type = label.toLowerCase() + 'contact'
  const contact = useMemo(() => org[type], [org, type])
  const changer = useCallback(
    (value, { token: key }) => {
      onChange({ ...contact, [key]: value }, { token: type })
    },
    [onChange, contact, type]
  )
  const onSave = useCallback(
    (args, value, good, bad) =>
      org.onSave(
        { ...args, token: type },
        { ...org[type], [args.token]: value },
        good,
        bad
      ),
    [org, type]
  )

  const [show, setShow] = useState(false)

  // <button className="tr mb3 mt4 clear disabled" onClick={() => setShow(true)}>
  //   {label} {!contact && !show && <i className="fa fa-plus" />}
  // </button>
  return (
    <>
      <button className="f6 tr b clear" onClick={() => setShow(!show)}>
        {label} {!contact && !show && <i className="fas fa-plus" />}
      </button>
      <div />
      {(contact || show) && (
        <>
          <div className="b tr mr2 items-center">Full Name:</div>
          <Input
            wrapClass="normal"
            value={contact?.name || ''}
            onChange={changer}
            onSave={onSave}
            component="data"
            token="name"
            placeholder="Full Name"
            makeVars={makeVars}
          />
          <div className="b tr mr2 items-center">Title:</div>
          <Input
            wrapClass="normal"
            value={contact?.title || ''}
            onChange={changer}
            onSave={onSave}
            component="data"
            token="title"
            placeholder="Title"
            makeVars={makeVars}
          />
          <div className="b tr mr2 items-center">Email:</div>
          <Input
            wrapClass="normal"
            value={contact?.email || ''}
            onChange={changer}
            onSave={onSave}
            component="data"
            token="email"
            placeholder="Email"
            makeVars={makeVars}
          />
          <div className="b tr mr2 items-center">Phone:</div>
          <Input
            wrapClass="normal"
            value={contact?.phone || ''}
            onChange={changer}
            onSave={onSave}
            component="data"
            token="phone"
            placeholder="Phone"
            makeVars={makeVars}
          />
        </>
      )}
    </>
  )
}
